/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Logger } from './Logger';
export type SocketIoEventKind = 'socketerror' | 'reconnecterror' | 'disconnect';
// Empty string means normal disconnect
export type DisconnectReasonKind = '' | 'dup' | 'unauthorized' | 'badrequest' | 'serviceunavailable' | SocketIoEventKind | 'unknown' | 'skypetoken-deprecated';

/**
 * Disconnect reason format sent when using the /v4/c protocol as part of the socket.io disconnect (`0::`),
 * e.g.
 * - `0::`
 * - `0:::dup`
 * - `0:::{"reason":"unauthorized","claims":"Bearer token_types=\"skype,aad,cae\""}`
 *
 * Additionally, it is also used internally for other socket.io events calling `TrouterFsm.onSocketDisconnect` (see usages).
 */
export class DisconnectReason {
    constructor(
        public readonly reason: DisconnectReasonKind,
        public readonly claims?: string,
        public readonly details?: string
    ) { }

    public static fromRawReason(rawReason: string, logger?: Logger): DisconnectReason {
        if (rawReason === '' || rawReason === 'dup') {
            return new DisconnectReason(rawReason);
        }

        try {
            const parsedReason = <Partial<DisconnectReason>>JSON.parse(rawReason);
            if (typeof parsedReason !== 'object' || parsedReason.reason === undefined) {
                logger?.error('invalid disconnect reason format');

                return new DisconnectReason('unknown', undefined, rawReason);
            }

            return new DisconnectReason(parsedReason.reason, parsedReason.claims, parsedReason.details);
        } catch (e) {
            // Non-JSON string from the `0::` socket.io message is just set "as is" as details
            return new DisconnectReason('disconnect', undefined, rawReason);
        }
    }

    public static fromSocketIoEventData(event: SocketIoEventKind, arg: unknown): DisconnectReason | undefined {
        return typeof arg === 'string' ? new DisconnectReason(event, undefined, arg) : undefined;
    }

    public toTelemetryString(): string | undefined {
        if (this.reason === 'socketerror' || this.reason === 'reconnecterror' || this.reason === 'disconnect') {
            return this.details;
        }

        return this.reason;
    }

}



// WEBPACK FOOTER //
// ./lib_temp/DisconnectReason.ts